import axios from "axios";
import { TOKEN_KEY, REFRESH_KEY } from "../constants";
import i18n from "../i18n";

// URL correcte : backend ecoute sur /api
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Langue active de l'interface au moment de la requete (pas celle
  // enregistree a l'inscription) : le backend l'utilise pour choisir
  // la langue des emails/SMS envoyes (OTP, mot de passe oublie...).
  config.headers["Accept-Language"] = i18n.language?.startsWith("en") ? "EN" : "FR";
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem(REFRESH_KEY);
      if (refresh) {
        try {
          const res = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken: refresh });
          localStorage.setItem(TOKEN_KEY, res.data.data.accessToken);
          original.headers.Authorization = `Bearer ${res.data.data.accessToken}`;
          return api(original);
        } catch { localStorage.clear(); window.location.href = "/login"; }
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register:        (data) => api.post("/auth/register",          data),
  verifyOtp:        (data) => api.post("/auth/verify-otp",        data),
  resendOtp:        (data) => api.post("/auth/resend-otp",        data),
  login:            (data) => api.post("/auth/login",             data),
  forgotPassword:   (data) => api.post("/auth/forgot-password",   data),
  verifyResetCode:  (data) => api.post("/auth/verify-reset-code", data),
  resetPassword:    (data) => api.post("/auth/reset-password",    data),
};
export const objectsApi = {
    declareLost: (data) => api.post("/objects/lost", data),
    declareFound: (data) => api.post("/objects/found", data),
    getMyLost: () => api.get("/objects/lost"),
    getMyFound: () => api.get("/objects/found"),
    getLostById: (id) => api.get(`/objects/lost/${id}`),
    getMyMatchings: () => api.get("/matching/my"),
};
export const matchingApi = {
  getForLost:    (id) => api.get(`/matching/lost/${id}`),
  confirmOwner:  (id) => api.post(`/matching/${id}/confirm-owner`),
  confirmFinder: (id) => api.post(`/matching/${id}/confirm-finder`),
};
export const restitutionApi = {
  submitProof:      (mId, data) => api.post(`/restitution/${mId}/proof`, data),
  confirmAndPay:    (mId, data) => api.post(`/restitution/${mId}/pay`,   data),
  confirmReception: (dId, data) => api.post(`/restitution/${dId}/confirm-reception`, data),
};
export const transactionsApi = {
  initiate:      (data)      => api.post("/transactions/initiate", data),
  respond:       (ref, data) => api.post(`/transactions/${ref}/respond`, data),
  getAll:        ()          => api.get("/transactions"),
  getPending:    ()          => api.get("/transactions/pending"),
  getByReference:(ref)       => api.get(`/transactions/${ref}`),
};
export const certificatesApi = {
  get:          (ref)        => api.get(`/certificates/${ref}`),
  verify:       (ref, hash)  => api.get(`/certificates/verify/${ref}/${hash}`),
  publicKey:    ()           => api.get(`/certificates/public-key`),
  downloadPdf:  (ref)        => api.get(`/certificates/${ref}/pdf`, { responseType: "blob" }),
};
export const adminApi = {
  getDashboard: () => api.get("/admin/dashboard"),
  getUsers: (params) => api.get("/admin/users", { params }),
  updateUserStatus: (id, active) => api.patch(`/admin/users/${id}/status`, { active }),
  getObjects: (params) => api.get("/admin/objects", { params }),
  getMatchings: (params) => api.get("/admin/matchings", { params }),
  getTransactions: (params) => api.get("/admin/transactions", { params }),
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.put("/admin/settings", data),
  getAuditLog: (params) => api.get("/admin/audit", { params }),
};
export default api;
